import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COL, type InboundRejected, type Notification } from '@eprintsmart/shared';
import { db } from '../firebase';

/**
 * The outbound message queue, and the invites that write to it.
 *
 * Worth a page of its own rather than a log line. Email fails quietly — a wrong
 * key, a revoked delegation, a mailbox over its daily cap — and the whole reason
 * this queue existed for months without anybody noticing it was never drained is
 * that nothing ever showed it. If deliveries stop again, this is where it shows.
 */

const fns = () => getFunctions(undefined, 'asia-south1');

/** Gmail's ceiling on the shared Workspace mailbox, counted in recipients. */
export const DAILY_SEND_LIMIT = 2000;

export function watchNotifications(
  cb: (rows: Notification[]) => void,
  max = 200,
): () => void {
  return onSnapshot(
    query(collection(db, COL.notifications), orderBy('createdAt', 'desc'), limit(max)),
    (snap) => cb(snap.docs.map((d) => ({ ...(d.data() as Notification), id: d.id }))),
  );
}

/**
 * What is waiting for a person, for the badge in the sidebar.
 *
 * THIS IS THE POINT OF THE WHOLE PAGE. Both of these fail silently: a delivery
 * that stopped working and a customer's reply that could not be placed look
 * exactly like nothing happening, and neither is visible unless somebody thinks
 * to go and look. The badge is what removes the "thinks to".
 *
 * Counts, not documents — the caller needs a number and the bodies are large
 * (a parked record keeps up to 20KB of raw email). The cap is why it is
 * `limit(CAP + 1)`: one more than the badge will ever print, so "20+" is known
 * to be true without reading a spam flood into the sidebar of every page.
 *
 * The parked query deliberately has no orderBy. Ordering would demand the
 * composite index the list uses; a bare equality rides the automatic one, and
 * nothing here cares what order they are in.
 *
 * This number can exceed the "failed" count shown on the page itself, which is
 * taken from the most recent 200 notifications. The badge is the honest one.
 */
export const ATTENTION_CAP = 20;

export function watchAttention(
  cb: (counts: { parked: number; failed: number }) => void,
): () => void {
  const counts = { parked: 0, failed: 0 };
  const emit = () => cb({ ...counts });

  const watch = (
    q: ReturnType<typeof query>,
    key: 'parked' | 'failed',
  ) => onSnapshot(q, (snap) => { counts[key] = snap.size; emit(); }, (err) => {
    // A badge must never take the whole console down with it, but a listener
    // that died is exactly the kind of silence this page exists to end.
    console.error(`attention badge: ${key} listener failed`, err);
  });

  const unsubs = [
    watch(query(
      collection(db, COL.inboundRejected),
      where('state', '==', 'PARKED'),
      limit(ATTENTION_CAP + 1),
    ), 'parked'),
    watch(query(
      collection(db, COL.notifications),
      where('deliveryState', '==', 'FAILED'),
      limit(ATTENTION_CAP + 1),
    ), 'failed'),
  ];
  return () => unsubs.forEach((u) => u());
}

/** Returns the outcome so the caller can say what happened without a round trip
 * through the snapshot listener — a resend that fails again should say so in
 * the same click, not look like it worked. */
export async function resendNotification(
  messageId: string,
): Promise<{ deliveryState: string; lastError: string | null }> {
  const result = await httpsCallable(fns(), 'resendNotification')({ messageId });
  return result.data as { deliveryState: string; lastError: string | null };
}

export async function inviteVendor(vendorId: string): Promise<void> {
  await httpsCallable(fns(), 'inviteVendor')({ vendorId });
}

export async function inviteArtist(input: { email: string }): Promise<void> {
  await httpsCallable(fns(), 'inviteArtist')(input);
}

/* ------------------------------------------------------------------ *
 * Email replies that could not be placed on an order
 * ------------------------------------------------------------------ */

/**
 * The parking bay, newest first.
 *
 * Only PARKED rows: a decided one is kept for ever as the record of what the
 * From-match rule actually rejected, but it is not work any more and a list
 * that never empties is a list nobody reads.
 */
export function watchParkedReplies(
  onChange: (replies: InboundRejected[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    query(
      collection(db, COL.inboundRejected),
      where('state', '==', 'PARKED'),
      orderBy('receivedAt', 'desc'),
      limit(100),
    ),
    (snap) => onChange(snap.docs.map((d) => d.data() as InboundRejected)),
    (err) => onError?.(err),
  );
}

/** Writes it onto the order's thread, attributed to whoever decided. */
export async function admitParkedReply(id: string): Promise<{ messageId: string }> {
  const result = await httpsCallable(fns(), 'admitInboundReply')({ id });
  return result.data as { messageId: string };
}

/** Marks it as not worth placing. Never deletes — see the callable. */
export async function dismissParkedReply(id: string): Promise<void> {
  await httpsCallable(fns(), 'dismissInboundReply')({ id });
}
